import {useEffect, useReducer} from 'react';
import {Content, DialogOverlay, Portal} from '@radix-ui/react-dialog';
import cx from 'classnames';
import Image from 'next/image';

import {Icon, TallyLogo, Text} from '#component/base';
import {ModalClose} from '#component/base/Modal';
import * as modalCss from '#component/base/Modal/Modal.css';
import {QuestionCard, QuestionIntroCard} from '#component/partial';
import {EventType, PageViewEvent, useAnalytics} from '#component/provider/AnalyticsProvider';
import {ImpactLevel} from '#constant/impactLevel';
import {useOnceEffect} from '#hook';
import {sprinkles} from '#style/sprinkles';

import {getAnswerSubmissionInput, initialState, reducer} from './CheckInModal.util';

import {CheckInModalProps} from './CheckInModal.types';

import * as css from './CheckInModal.css';

const cardClassnames = [css.card0, css.card1, css.card2, css.card3, css.card4];

export const CheckInModal = ({endedOn, onSubmit, questions = [], startedOn}: CheckInModalProps) => {
  const {track} = useAnalytics();
  const [{answers, shuffle, shuffleDirection, step}, dispatch] = useReducer(reducer, initialState);
  const questionsCount = questions.length;
  const stackSize = Math.max(0, Math.min(5, questionsCount + 1 - step));

  const handleChoose = ({id, answerBoolean}: {id: string; answerBoolean: boolean}) => {
    dispatch({type: 'SubmitAnswer', payload: {answerBoolean, id}});

    if (step === questionsCount) {
      const answerInput = getAnswerSubmissionInput({...answers, [id]: answerBoolean});
      onSubmit?.(answerInput);
    }
  };

  useEffect(() => {
    const timeout = shuffle ? setTimeout(() => dispatch({type: 'StopShuffle'}), 725) : undefined;

    return () => clearTimeout(timeout);
  }, [shuffle]);

  useOnceEffect(() => {
    track<PageViewEvent>({evtName: EventType.PageView, pageName: 'Checkin cards'});
  });

  return (
    <Portal>
      <DialogOverlay />
      <div className={modalCss.dialogRoot({layout: 'fullScreen'})}>
        <Content
          className={cx(
            modalCss.dialogContent({layout: 'fullScreen', variant: 'dark'}),
            css.dialogContent,
          )}>
          <div className={css.contentWrapper}>
            <div className={css.header}>
              {step > 0 ? (
                <>
                  <button
                    className={css.button}
                    onClick={() => dispatch({type: 'SetStep', payload: step - 1})}>
                    <Icon
                      aria-label="Go back to previous question"
                      color="cotton"
                      name="CaretLeft"
                    />
                  </button>

                  <Text
                    as="h1"
                    className={sprinkles({grow: 1, w: 'full'})}
                    color="white"
                    textAlign="center"
                    variant="label">
                    {`Question ${Math.min(questionsCount, step)} of ${questionsCount}`}
                  </Text>
                </>
              ) : (
                <TallyLogo color="cotton" size={74} />
              )}

              <ModalClose className={css.button}>
                <Icon aria-label="Exit Check-in" color="cotton" name="X" />
              </ModalClose>
            </div>

            <main className={css.body}>
              <div className={css.stack}>
                {shuffle && shuffleDirection === 'right' && step > 0 && (
                  <span className={css.thumbsUp}>
                    <Image alt="" aria-hidden src="/image/checkin/thumbs-up.svg" layout="fill" />
                  </span>
                )}

                {Array.from(Array(stackSize), (_, i) => {
                  const question = questions[step - 1 + i];
                  const renderIntro = i === 0 && step === 0;
                  const renderQuestion = !renderIntro && i <= 1 && !!question;

                  return (
                    <div className={cx(cardClassnames[i], shuffleDirection, {shuffle})} key={i}>
                      {renderQuestion && (
                        <QuestionCard
                          className={cx({[css.hidden]: i > 0 ? !shuffle : false})}
                          context={question.context}
                          contextTitle={question.healthFactor?.title}
                          healthFactorId={question.healthFactor?.id}
                          id={question.id}
                          imageUrl={question.imageUrl}
                          impactLevel={
                            question.healthFactor ? (question.impactLevel as ImpactLevel) : null
                          }
                          key={question.id}
                          onChoose={handleChoose}
                          title={question.title}
                        />
                      )}

                      {renderIntro && (
                        <QuestionIntroCard
                          onClick={() => dispatch({type: 'StartShuffle'})}
                          startedOn={startedOn}
                          endedOn={endedOn}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </main>
          </div>
        </Content>
      </div>
    </Portal>
  );
};
